/**
 * ModuleLockedView — tela premium exibida quando o módulo não está contratado.
 * Inclui CTA que abre o modal de contratação (mesmo das Configurações).
 */
import {useCallback, useState} from "react";
import {Box, Button, Chip, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {moduleBillingService, type ModuleSubscriptionSummary} from "../module-billing.service";
import {
  ModuleSubscribeDialog,
  ModuleSubscribeToast,
  useModuleSubscribe,
} from "./module-subscribe-dialog";
import {useModuleAccess} from "../module-access";

// ─── Types ────────────────────────────────────────────────────────────────────
type Props = {
  /** Nome amigável do módulo, ex: "Contas a pagar" */
  moduleName: string;
  /** Subtítulo exibido abaixo do nome */
  details: string;
  /**
   * ID do grupo do módulo (ex: "accounts_payable", "commissions").
   * Usado para abrir o modal já no módulo correto.
   * Se omitido, o botão navega para /configuracoes.
   */
  groupId?: string;
  /** Lista de features que aparecem como bullet points visuais */
  features?: string[];
};

// ─── Visual tokens ────────────────────────────────────────────────────────────
const SYMPL_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)";
const SYMPL_GRADIENT_BG = "linear-gradient(135deg, rgba(13,148,136,0.08) 0%, rgba(15,118,110,0.04) 100%)";

// Animação de pulso no ícone
const LOCK_KEYFRAMES = `
@keyframes sympl-lock-pulse {
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.9; }
  50% { transform: scale(1.06) rotate(-3deg); opacity: 1; }
}
`;

// ─── Feature bullet ───────────────────────────────────────────────────────────
function FeatureBullet({label}: {label: string}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <CheckCircleOutlineIcon sx={{color: "#0d9488", fontSize: 18, flexShrink: 0}} />
      <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
        {label}
      </Typography>
    </Stack>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export const ModuleLockedView = ({moduleName, details, groupId, features = []}: Props) => {
  const moduleAccess = useModuleAccess();

  // Busca o summary para passar ao modal (necessário para open())
  const [summary, setSummary] = useState<ModuleSubscriptionSummary | null>(null);

  const reloadSubscription = useCallback(async () => {
    try {
      const s = await moduleBillingService.getSummary();
      setSummary(s);
      // Recarrega o moduleAccess (via window reload simples — o hook já escuta o Firestore)
    } catch {/* silencioso */}
  }, []);

  const {dialogProps, open: openDialog, toast, onToastClose} = useModuleSubscribe(reloadSubscription);

  const handleCTA = useCallback(async () => {
    if (!groupId) {
      window.location.href = "/configuracoes";
      return;
    }
    // Carrega o summary se ainda não tiver
    let sub = summary;
    if (!sub) {
      try {
        sub = await moduleBillingService.getSummary();
        setSummary(sub);
      } catch {
        sub = {billingAnchorDay: null, billingCycleEndsAt: null, modules: {}};
      }
    }
    void openDialog(groupId, sub);
  }, [groupId, summary, openDialog]);

  return (
    <>
      {/* Inject keyframes */}
      <style>{LOCK_KEYFRAMES}</style>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "55vh",
          px: {xs: 2, sm: 4},
          py: {xs: 4, sm: 6},
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 540,
            borderRadius: 4,
            background: SYMPL_GRADIENT_BG,
            border: "1px solid",
            borderColor: alpha("#0d9488", 0.2),
            boxShadow: "0 8px 40px rgba(13,148,136,0.10), 0 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Detalhe decorativo superior */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: SYMPL_GRADIENT,
            }}
          />

          {/* Detalhe decorativo de fundo */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(20,184,166,0.12) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <Stack spacing={3} sx={{p: {xs: 3, sm: 4}, pt: {xs: 3.5, sm: 4.5}}}>
            {/* Ícone + chip */}
            <Stack direction="row" alignItems="flex-start" spacing={2.5}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 3,
                  background: SYMPL_GRADIENT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 8px 24px rgba(13,148,136,0.35)",
                  animation: "sympl-lock-pulse 3s ease-in-out infinite",
                }}
              >
                <LockOutlinedIcon sx={{color: "#fff", fontSize: 30}} />
              </Box>

              <Box sx={{flex: 1, pt: 0.5}}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.5}}>
                  <Chip
                    label="Módulo Premium"
                    size="small"
                    sx={{
                      background: SYMPL_GRADIENT,
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "0.68rem",
                      height: 22,
                      letterSpacing: "0.04em",
                    }}
                  />
                </Stack>
                <Typography
                  variant="h5"
                  fontWeight={900}
                  sx={{letterSpacing: "-0.02em", lineHeight: 1.2, color: "text.primary"}}
                >
                  {moduleName}
                </Typography>
              </Box>
            </Stack>

            {/* Descrição */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{lineHeight: 1.7, fontSize: "0.97rem"}}
            >
              {details}
            </Typography>

            {/* Features */}
            {features.length > 0 && (
              <Stack spacing={1.25}>
                {features.map((f) => (
                  <FeatureBullet key={f} label={f} />
                ))}
              </Stack>
            )}

            {/* Divider sutil */}
            <Box
              sx={{
                height: 1,
                background: `linear-gradient(90deg, transparent, ${alpha("#0d9488", 0.25)}, transparent)`,
              }}
            />

            {/* CTA */}
            <Stack spacing={1.5}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<RocketLaunchIcon />}
                onClick={() => void handleCTA()}
                sx={{
                  background: SYMPL_GRADIENT,
                  fontWeight: 800,
                  fontSize: "1rem",
                  py: 1.5,
                  borderRadius: 2.5,
                  letterSpacing: "0.01em",
                  boxShadow: "0 8px 24px rgba(13,148,136,0.35)",
                  textTransform: "none",
                  "&:hover": {
                    boxShadow: "0 12px 32px rgba(13,148,136,0.45)",
                    filter: "brightness(1.05)",
                  },
                  transition: "box-shadow 0.2s, filter 0.2s",
                }}
              >
                Contratar agora
              </Button>

              <Typography
                variant="caption"
                color="text.disabled"
                align="center"
                sx={{display: "block", lineHeight: 1.5}}
              >
                Ativação imediata · Cancele quando quiser
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Modal de contratação (idêntico ao das Configurações) */}
      <ModuleSubscribeDialog {...dialogProps} />
      <ModuleSubscribeToast {...toast} onClose={onToastClose} />
    </>
  );
};
